/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { useCallback, useEffect, useState } from "react";
import {
	FloatingPanelID,
	FloatingPanelModel,
	IFloatingPanelModelConstructor,
	TGetFloatingPanelOpenParams,
	useFloatingPanelContext
} from "../components/FloatingPanel";
import { offEvent, onEvent, isFunction } from "@adapptio/lib-shared";

export interface IUseFloatingPanel<Params extends unknown> {
	openPanel: (paramsOverride?: Params) => void;
	closePanel: () => void;
	togglePanel: () => void;
	opened: boolean;
}

export function useFloatingPanel<
	TFloatingPanelModel extends FloatingPanelModel,
	Params extends TGetFloatingPanelOpenParams<TFloatingPanelModel>
>(
	ctor: IFloatingPanelModelConstructor<TFloatingPanelModel>,
	panelId: FloatingPanelID,
	params: Params
): IUseFloatingPanel<Params> {
	const { manager } = useFloatingPanelContext();

	const isPanelOpened = useCallback((id = panelId) => manager.isOpened(id), [ manager, panelId ]);

	const [ opened, setOpened ] = useState(() => isPanelOpened());

	const openPanel = useCallback(
		(openParams = params) => {
			manager.open(ctor, panelId, openParams);
		},
		[ manager, params, panelId, ctor ]
	);

	const closePanel = useCallback(() => {
		const panel = manager.getFloatingPanelById(panelId);

		if (isFunction(panel?.close)) {
			panel.close();
			setOpened(false);
		}
	}, [ manager, panelId ]);

	const togglePanel = useCallback(() => {
		if (isPanelOpened()) {
			closePanel();
		} else {
			openPanel();
		}
	}, [ manager, panelId, openPanel, closePanel, isPanelOpened ]);

	useEffect(() => {
		const handleEntriesChange = () => {
			if (opened !== isPanelOpened(panelId)) {
				setOpened(isPanelOpened(panelId));
			}
		};

		onEvent(manager.onEntriesChange, handleEntriesChange);

		return () => {
			offEvent(manager.onEntriesChange, handleEntriesChange);
		};
	}, [ manager, panelId, opened, isPanelOpened ]);

	return { openPanel, closePanel, togglePanel, opened };
}
